import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faAddressCard,
  faImage,
  faGlobe,
  faFloppyDisk,
  faRotateLeft,
  faCircleCheck,
  faTriangleExclamation,
  faChalkboardUser,
} from '@fortawesome/free-solid-svg-icons';
import './EditTeacherInfo.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

// Same fields + same starting values as before (empty strings instead of null so inputs stay controlled)
const fromTeacher = (t) => ({
  firstName: t.FirstName ?? '',
  lastName: t.LastName ?? '',
  email: t.Email ?? '',
  mobileNumber: t.MobileNumber ?? '',
  birthday: t.Birthday ? String(t.Birthday).split('T')[0] : '',
  image: t.Image ?? '',
  username: t.Username ?? '',
});

const Preview = ({ src }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  return (
    <span className="rmaet-preview">
      {src && !broken ? <img src={src} alt="" onError={() => setBroken(true)} /> : <FontAwesomeIcon icon={faChalkboardUser} />}
    </span>
  );
};

const EditTeacherInfo = ({ teacher, setTeacher }) => {
  const [formData, setFormData] = useState(() => fromTeacher(teacher));
  const [offsetTeacher, setOffsetTeacher] = useState(teacher.Offset ?? '');
  const [offsetTeacherList, setOffsetTeacherList] = useState([]);
  const [listError, setListError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const teacherId = teacher?.IdUser;

  // ---------- Time zones (same endpoint) — once per teacher ----------
  useEffect(() => {
    if (!teacherId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/studentprofile/OffsetList`, auth());
        if (!cancelled) {
          setOffsetTeacherList(Array.isArray(res.data) ? res.data : []);
          setListError('');
        }
      } catch (err) {
        console.error('Error fetching timezones:', err);
        if (!cancelled) setListError('Couldn’t load the time zones.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teacherId]);

  // success message hides after 3s (same as before)
  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(false), 3000);
    return () => clearTimeout(t);
  }, [success]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
    setSuccess(false);
  };

  const original = fromTeacher(teacher);
  const dirty =
    Object.keys(original).some((k) => String(formData[k] ?? '') !== String(original[k] ?? '')) ||
    String(offsetTeacher ?? '') !== String(teacher.Offset ?? '');

  const resetForm = () => {
    setFormData(fromTeacher(teacher));
    setOffsetTeacher(teacher.Offset ?? '');
    setError('');
  };

  // ---------- Save (same endpoint + same body) ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    try {
      const res = await axios.put(
        `${API}/api/admin/teacherprofile/update/${teacher.IdUser}`,
        { ...formData, Offset: offsetTeacher },
        auth()
      );
      const saved = res.data;
      // same as before when the server sends the teacher back; otherwise keep the page working with what was typed
      if (saved && typeof saved === 'object' && 'FirstName' in saved) {
        setTeacher(saved);
      } else {
        setTeacher((t) => ({
          ...t,
          FirstName: formData.firstName,
          LastName: formData.lastName,
          Email: formData.email,
          MobileNumber: formData.mobileNumber,
          Birthday: formData.birthday || null,
          Image: formData.image,
          Username: formData.username,
          Offset: offsetTeacher,
        }));
      }
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Update failed.');
    } finally {
      setLoading(false);
    }
  };

  const field = (name, label, type = 'text', extra = {}) => (
    <label className="rmaet-field">
      <span>{label}</span>
      <input type={type} name={name} value={formData[name]} onChange={handleChange} disabled={loading} {...extra} />
    </label>
  );

  return (
    <form className="rmaet" onSubmit={handleSubmit}>
      <div className="rmaet-grid">
        <section className="rmaet-card">
          <h3>
            <FontAwesomeIcon icon={faUser} /> Personal details
          </h3>
          <div className="rmaet-row">
            {field('firstName', 'First name', 'text', { required: true, autoComplete: 'off' })}
            {field('lastName', 'Last name', 'text', { required: true, autoComplete: 'off' })}
          </div>
          {field('birthday', 'Birthday', 'date')}
        </section>

        <section className="rmaet-card">
          <h3>
            <FontAwesomeIcon icon={faAddressCard} /> Account & contact
          </h3>
          {field('email', 'Email', 'email', { required: true, autoComplete: 'off' })}
          <div className="rmaet-row">
            {field('username', 'Username', 'text', { required: true, autoComplete: 'off' })}
            {field('mobileNumber', 'Mobile number', 'tel', { required: true, autoComplete: 'off' })}
          </div>
        </section>

        <section className="rmaet-card">
          <h3>
            <FontAwesomeIcon icon={faImage} /> Photo
          </h3>
          <div className="rmaet-photo">
            <Preview src={formData.image} />
            {field('image', 'Image URL', 'url', { placeholder: 'https://…' })}
          </div>
        </section>

        <section className="rmaet-card">
          <h3>
            <FontAwesomeIcon icon={faGlobe} /> Time zone
          </h3>
          <label className="rmaet-field">
            <span>Offset time zone</span>
            <select value={offsetTeacher ?? ''} onChange={(e) => setOffsetTeacher(e.target.value)} disabled={loading}>
              <option value="">Select time zone</option>
              {offsetTeacherList.map((o) => (
                <option key={o.Id} value={o.Id}>
                  {o.Timezone} ({o.TimeHours >= 0 ? '+' : ''}
                  {o.TimeHours})
                </option>
              ))}
            </select>
            {listError ? <small className="is-error">{listError}</small> : <small>Used to show class times in the teacher’s local time.</small>}
          </label>
        </section>
      </div>

      {/* ---------- Save bar ---------- */}
      <div className="rmaet-bar" aria-live="polite">
        <p className={error ? 'is-error' : success ? 'is-ok' : ''}>
          {error ? (
            <>
              <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
            </>
          ) : success ? (
            <>
              <FontAwesomeIcon icon={faCircleCheck} /> Information updated.
            </>
          ) : dirty ? (
            'You have unsaved changes.'
          ) : (
            'Everything is saved.'
          )}
        </p>
        <button type="button" className="rmaet-btn rmaet-btn--ghost" onClick={resetForm} disabled={loading || !dirty}>
          <FontAwesomeIcon icon={faRotateLeft} /> Undo
        </button>
        <button type="submit" className="rmaet-btn" disabled={loading || !dirty}>
          <FontAwesomeIcon icon={faFloppyDisk} /> {loading ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>
  );
};

export default EditTeacherInfo;